<template>
  <div class="automation-create-page">
    <section class="setup-hero">
      <div>
        <span class="setup-kicker">Create automation</span>
        <h1>New Automation</h1>
        <p>
          Connect a page, choose the queue type, upload a ZIP, and let PostMate
          build the schedule.
        </p>
      </div>
      <div class="setup-summary">
        <span>{{ selectedPostTypeLabel }}</span>
        <strong>{{ selectedPageName || "No page selected" }}</strong>
      </div>
    </section>

    <b-form @submit.prevent="submitAutomation" class="setup-grid">
      <section class="setup-panel main-panel">
        <div class="panel-head">
          <div>
            <span>Step 1</span>
            <h2>Identity</h2>
          </div>
          <b-badge variant="light">Required</b-badge>
        </div>

        <div class="field-grid">
          <b-form-group label="Automation name" label-for="name">
            <b-form-input
              id="name"
              v-model.trim="form.name"
              required
              placeholder="e.g. Spring campaign queue"
              class="modern-input"
            />
          </b-form-group>

          <b-form-group label="Access token" label-for="accessToken">
            <b-input-group>
              <b-form-input
                id="accessToken"
                v-model.trim="form.accessToken"
                required
                placeholder="Paste token"
                class="modern-input"
              />
              <b-input-group-append>
                <b-button
                  type="button"
                  variant="primary"
                  class="fetch-pages-btn"
                  :disabled="loadingPages || !form.accessToken"
                  @click="fetchPages"
                >
                  <b-spinner v-if="loadingPages" small></b-spinner>
                  <span v-else>Get Pages</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>

        <div v-if="user.allowCustomProxy" class="option-row">
          <div>
            <strong>Custom proxy</strong>
            <span>
              Off uses the saved proxy attached to the selected token.
            </span>
          </div>
          <b-form-checkbox v-model="form.useCustomProxy" switch />
        </div>

        <div v-if="form.useCustomProxy" class="nested-panel">
          <div class="field-grid two">
            <b-form-group label="Proxy username" label-for="proxyUsername">
              <b-form-input
                id="proxyUsername"
                v-model.trim="form.proxyUsername"
                class="modern-input"
                placeholder="Username"
              />
            </b-form-group>
            <b-form-group label="Proxy password" label-for="proxyPassword">
              <b-form-input
                id="proxyPassword"
                v-model="form.proxyPassword"
                type="password"
                class="modern-input"
                placeholder="Password"
              />
            </b-form-group>
            <b-form-group label="Host" label-for="host">
              <b-form-input
                id="host"
                v-model.trim="form.host"
                class="modern-input"
                placeholder="Host"
              />
            </b-form-group>
            <b-form-group label="Port" label-for="port">
              <b-form-input
                id="port"
                v-model.trim="form.port"
                class="modern-input"
                placeholder="Port"
              />
            </b-form-group>
          </div>
        </div>

        <div
          v-if="retryCount > 0 && !loadingPages && !pageOptions.length"
          class="empty-state"
        >
          <b-icon icon="exclamation-circle"></b-icon>
          No pages found for this token.
        </div>
      </section>

      <section class="setup-panel" :class="{ muted: !pageOptions.length }">
        <div class="panel-head">
          <div>
            <span>Step 2</span>
            <h2>Destination</h2>
          </div>
          <b-badge :variant="pageOptions.length ? 'primary' : 'light'">
            {{ pageOptions.length ? `${pageOptions.length} pages` : "Locked" }}
          </b-badge>
        </div>

        <div v-if="!pageOptions.length" class="locked-callout">
          <b-icon icon="lock"></b-icon>
          <div>
            <strong>Fetch pages to unlock this section</strong>
            <span>
              Enter an access token and click Get Pages. Page and post type
              are required before a job can be created.
            </span>
          </div>
        </div>

        <div class="field-grid two">
          <b-form-group label="Facebook page" label-for="pageId">
            <b-form-select
              id="pageId"
              v-model="form.pageId"
              :options="pageSelectOptions"
              :disabled="!pageOptions.length"
              required
              class="modern-select"
              @change="updatePageDetails"
            />
          </b-form-group>

          <b-form-group label="Post type" label-for="postType">
            <b-form-select
              id="postType"
              v-model="form.postType"
              :options="postTypeSelectOptions"
              :disabled="!pageOptions.length"
              required
              class="modern-select"
            />
          </b-form-group>
        </div>
      </section>

      <section class="setup-panel" :class="{ muted: !canConfigureQueue }">
        <div class="panel-head">
          <div>
            <span>Step 3</span>
            <h2>Queue Rules</h2>
          </div>
          <b-badge variant="light">{{ cadenceLabel }}</b-badge>
        </div>

        <div v-if="!canConfigureQueue" class="locked-callout">
          <b-icon icon="lock"></b-icon>
          <div>
            <strong>Select destination first</strong>
            <span>
              Choose a Facebook page and post type before setting cadence,
              captions, story mode, or optional extras.
            </span>
          </div>
        </div>

        <template v-else>
        <div class="cadence-grid">
          <b-form-group
            label="Minimum interval"
            label-for="intervalMin"
            class="cadence-field"
          >
            <b-input-group append="min" class="cadence-input-group">
              <b-form-input
                id="intervalMin"
                type="number"
                min="1"
                required
                v-model="form.intervalMin"
                placeholder="1"
                class="modern-input"
              />
            </b-input-group>
          </b-form-group>

          <b-form-group
            label="Maximum interval"
            label-for="intervalMax"
            class="cadence-field"
          >
            <b-input-group append="min" class="cadence-input-group">
              <b-form-input
                id="intervalMax"
                type="number"
                min="1"
                required
                v-model="form.intervalMax"
                placeholder="5"
                class="modern-input"
              />
            </b-input-group>
          </b-form-group>
        </div>

        <div v-if="isPhoto" class="option-stack caption-mode-stack">
          <button
            type="button"
            class="choice-card"
            :class="{ active: form.customCaptionsEnabled }"
            @click="form.customCaptionsEnabled = true"
          >
            <b-icon icon="card-text"></b-icon>
            <span>
              <strong>Custom captions</strong>
              <small>Upload first, then write captions per photo.</small>
            </span>
          </button>
          <button
            type="button"
            class="choice-card"
            :class="{ active: !form.customCaptionsEnabled }"
            @click="form.customCaptionsEnabled = false"
          >
            <b-icon icon="file-earmark-text"></b-icon>
            <span>
              <strong>Classic captions</strong>
              <small>Use filename captions or publish without captions.</small>
            </span>
          </button>
        </div>

        <div class="publish-options-grid">
          <b-form-group
            v-if="isPhoto && form.customCaptionsEnabled"
            label="Empty caption fallback"
            label-for="captionFallback"
          >
            <b-form-select
              id="captionFallback"
              v-model="form.captionFallback"
              :options="captionFallbackOptions"
              class="modern-select"
            />
          </b-form-group>

          <b-form-group
            v-if="!isPhoto || !form.customCaptionsEnabled"
            label="Use filename as caption"
            label-for="fileNameAsDescription"
          >
            <b-form-select
              id="fileNameAsDescription"
              v-model="form.fileNameAsDescription"
              :options="yesNoOptions"
              class="modern-select"
            />
          </b-form-group>

          <div
            v-if="form.postType === 1 && permissions.allowStoryPost"
            class="story-toggle-card"
            :class="{ active: form.isStory }"
          >
            <div>
              <strong>Post as story</strong>
              <span>Use story publishing flow for this reel ZIP.</span>
            </div>
            <b-form-checkbox
              v-model="form.isStory"
              switch
              class="story-switch"
            />
          </div>
        </div>

        <div v-if="permissions.allowCollaborations" class="nested-panel">
          <div class="panel-mini-title">Optional extras</div>
          <div class="field-grid two">
            <b-form-group label="Collab name" label-for="collabName">
              <b-form-input
                id="collabName"
                v-model.trim="form.collabName"
                class="modern-input"
                placeholder="Creator name"
              />
            </b-form-group>
            <b-form-group label="Collab ID" label-for="collabId">
              <b-form-input
                id="collabId"
                v-model.trim="form.collabId"
                class="modern-input"
                placeholder="Creator ID"
              />
            </b-form-group>
          </div>
          <b-form-group label="Hashtag pool" label-for="hashtagPool">
            <b-form-textarea
              id="hashtagPool"
              v-model="form.hashtagPool"
              rows="3"
              class="modern-input"
              placeholder="#one #two #three"
            />
          </b-form-group>
        </div>
        </template>
      </section>

      <aside class="setup-panel side-panel" :class="{ muted: !canConfigureQueue }">
        <div class="panel-head">
          <div>
            <span>Step 4</span>
            <h2>Upload</h2>
          </div>
          <b-badge :variant="form.file ? 'success' : 'light'">
            {{ form.file ? "Ready" : "ZIP" }}
          </b-badge>
        </div>

        <div class="dropzone-shell" :class="{ locked: !canConfigureQueue }">
          <vue-dropzone
            id="customdropzone"
            ref="dropzone"
            :options="dropzoneOptions"
            @vdropzone-file-added="handleFileAdded"
            @vdropzone-removed-file="handleFileRemoved"
            class="custom-dropzone"
          >
            <div class="dropzone-custom-content">
              <b-icon icon="cloud-upload"></b-icon>
              <h3 class="dropzone-custom-title">Drop ZIP here</h3>
              <div class="subtitle">Photos, reels, or story media</div>
            </div>
          </vue-dropzone>
          <div v-if="!canConfigureQueue" class="dropzone-lock">
            <b-icon icon="lock"></b-icon>
            <strong>Upload unlocks after destination is selected</strong>
          </div>
        </div>

        <div v-if="uploadProgress > 0" class="upload-progress">
          <b-progress
            :value="uploadProgress"
            show-value
            height="1rem"
            animated
          />
        </div>

        <div class="submission-card">
          <div>
            <strong>{{ submitButtonText }}</strong>
            <span>{{ submissionHint }}</span>
          </div>
          <b-button
            type="submit"
            variant="primary"
            class="submit-btn"
            :disabled="submitting || !canSubmit"
          >
            <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
            Create
          </b-button>
        </div>

        <div v-if="validationErrors.length" class="validation-hint">
          <b-icon icon="info-circle"></b-icon>
          <span>{{ validationErrors[0] }}</span>
        </div>
      </aside>
    </b-form>
  </div>
</template>

<style scoped>
.automation-create-page {
  color: #172033;
}

.setup-hero,
.setup-panel {
  background: #fff;
  border: 1px solid #dfe7f1;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, 0.055);
}

.setup-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1.35rem;
}

.setup-kicker,
.panel-head span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.setup-hero h1,
.panel-head h2 {
  margin: 0.15rem 0 0;
  color: #0f172a;
  font-weight: 850;
}

.setup-hero h1 {
  font-size: 2rem;
}

.setup-hero p {
  max-width: 680px;
  margin: 0.45rem 0 0;
  color: #64748b;
  font-size: 1rem;
}

.setup-summary {
  min-width: 240px;
  padding: 0.9rem;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background: #f8fbff;
}

.setup-summary span,
.setup-summary strong {
  display: block;
}

.setup-summary span {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 800;
}

.setup-summary strong {
  margin-top: 0.2rem;
  color: #0f172a;
  font-size: 1.05rem;
}

.setup-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 1rem;
}

.setup-panel {
  padding: 1.1rem;
}

.main-panel {
  grid-column: 1;
}

.side-panel {
  grid-column: 2;
  grid-row: 1 / span 3;
  align-self: start;
  position: sticky;
  top: 1rem;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-head h2 {
  font-size: 1.2rem;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.field-grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cadence-grid,
.publish-options-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.cadence-grid {
  margin-bottom: 0.95rem;
}

.cadence-field {
  margin-bottom: 0;
}

.cadence-field ::v-deep label {
  margin-bottom: 0.35rem;
  color: #334155;
  font-size: 0.88rem;
  font-weight: 800;
}

.cadence-input-group {
  overflow: hidden;
  border: 1px solid #d7e1ec;
  border-radius: 12px;
  background: #fff;
}

.cadence-input-group .modern-input {
  border: 0;
  border-radius: 0;
}

.cadence-input-group ::v-deep .input-group-text {
  min-width: 58px;
  justify-content: center;
  border: 0;
  border-left: 1px solid #d7e1ec;
  background: #f8fafc;
  color: #475569;
  font-size: 0.9rem;
  font-weight: 800;
}

.modern-input,
.modern-select {
  display: block;
  width: 100%;
  min-height: 44px;
  border: 1px solid #d7e1ec;
  border-radius: 10px;
  color: #0f172a;
  box-shadow: none;
  font-size: 0.95rem;
}

.modern-select {
  padding: 0.6rem 2.25rem 0.6rem 0.8rem;
  background-color: #fff;
  background-position: right 0.8rem center;
  background-size: 14px 10px;
}

.modern-input:focus,
.modern-select:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 0.18rem rgba(22, 119, 255, 0.12);
}

.modern-input:disabled,
.modern-select:disabled {
  color: #94a3b8;
  background-color: #f1f5f9;
  cursor: not-allowed;
}

.fetch-pages-btn,
.submit-btn {
  min-height: 44px;
  border-radius: 10px;
  font-weight: 800;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.85rem;
  padding: 0.85rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.option-row.compact {
  margin: 0;
  height: 100%;
}

.option-row strong,
.option-row span {
  display: block;
}

.option-row strong {
  color: #0f172a;
}

.option-row span {
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.86rem;
}

.nested-panel {
  margin-top: 0.9rem;
  padding: 0.8rem;
  border: 1px solid #edf2f7;
  border-radius: 13px;
  background: #fbfdff;
}

.panel-mini-title {
  margin-bottom: 0.65rem;
  color: #475569;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.nested-panel .field-grid {
  gap: 0.7rem;
}

.nested-panel ::v-deep label {
  margin-bottom: 0.3rem;
  color: #334155;
  font-size: 0.86rem;
  font-weight: 800;
}

.empty-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
  color: #92400e;
  font-weight: 750;
}

.muted {
  background: #f8fafc;
  border-style: dashed;
}

.locked-callout {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-bottom: 1rem;
  padding: 0.85rem;
  border: 1px solid #dbeafe;
  border-radius: 13px;
  background: #eff6ff;
  color: #1d4ed8;
}

.locked-callout svg {
  flex: 0 0 auto;
  margin-top: 0.15rem;
}

.locked-callout strong,
.locked-callout span {
  display: block;
}

.locked-callout strong {
  color: #1e3a8a;
  font-size: 0.94rem;
}

.locked-callout span {
  margin-top: 0.15rem;
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.35;
}

.option-stack {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.caption-mode-stack {
  margin-top: 0.2rem;
}

.choice-card {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  width: 100%;
  min-height: 86px;
  padding: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  color: #172033;
  text-align: left;
}

.choice-card.active {
  border-color: #1677ff;
  background: #f3f8ff;
  box-shadow: inset 0 0 0 1px rgba(22, 119, 255, 0.12);
}

.choice-card svg {
  margin-top: 0.15rem;
  color: #1677ff;
}

.choice-card strong,
.choice-card small {
  display: block;
}

.publish-options-grid {
  align-items: stretch;
  margin-top: 0.85rem;
}

.publish-options-grid .form-group {
  margin-bottom: 0;
}

.publish-options-grid ::v-deep label {
  margin-bottom: 0.35rem;
  color: #334155;
  font-size: 0.88rem;
  font-weight: 800;
}

.story-toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  min-height: 70px;
  padding: 0.8rem 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.story-toggle-card.active {
  border-color: #1677ff;
  background: #f3f8ff;
}

.story-toggle-card strong,
.story-toggle-card span {
  display: block;
}

.story-toggle-card strong {
  color: #0f172a;
  font-size: 0.95rem;
}

.story-toggle-card span {
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.35;
}

.story-switch {
  flex: 0 0 auto;
}

.choice-card small {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.35;
}

.dropzone-shell {
  position: relative;
}

.dropzone-shell.locked .custom-dropzone {
  opacity: 0.45;
  pointer-events: none;
}

.custom-dropzone {
  border: 2px dashed #bfd0e4 !important;
  border-radius: 14px !important;
  background: #f8fafc !important;
  min-height: 220px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropzone-lock {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 1rem;
  border-radius: 14px;
  color: #334155;
  background: rgba(248, 250, 252, 0.86);
  text-align: center;
}

.dropzone-lock svg {
  color: #1677ff;
}

.dropzone-lock strong {
  max-width: 260px;
  font-size: 0.9rem;
}

.custom-dropzone:hover {
  border-color: #1677ff !important;
  background: #f1f7ff !important;
}

.dropzone-custom-content {
  text-align: center;
}

.dropzone-custom-content svg {
  width: 34px;
  height: 34px;
  margin-bottom: 0.5rem;
  color: #1677ff;
}

.dropzone-custom-title {
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 850;
}

.subtitle {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.86rem;
}

.upload-progress {
  margin-top: 1rem;
}

.submission-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #edf2f7;
}

.submission-card strong,
.submission-card span {
  display: block;
}

.submission-card strong {
  color: #0f172a;
}

.submission-card span {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.84rem;
}

.validation-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin-top: 0.85rem;
  padding: 0.7rem;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  color: #9a3412;
  background: #fff7ed;
  font-size: 0.84rem;
  font-weight: 750;
  line-height: 1.35;
}

.validation-hint svg {
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.vue-dropzone > .dz-preview .dz-image {
  border-radius: 10px !important;
}

.vue-dropzone > .dz-preview {
  margin: 10px !important;
}

.vue-dropzone > .dz-preview .dz-remove {
  color: #dc2626 !important;
  text-decoration: none !important;
  font-weight: 700;
  margin-top: 8px;
}

@media (max-width: 991.98px) {
  .setup-hero,
  .setup-grid,
  .field-grid.two,
  .cadence-grid,
  .publish-options-grid,
  .option-stack {
    grid-template-columns: 1fr;
  }

  .setup-hero {
    display: grid;
  }

  .setup-grid {
    display: grid;
  }

  .side-panel,
  .main-panel {
    grid-column: auto;
    grid-row: auto;
    position: static;
  }
}
</style>

<script>
import router from "@/router";
import axios from "axios";
import vueDropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  components: {
    vueDropzone,
  },
  data() {
    return {
      form: {
        name: "",
        accessToken: "",
        pageId: "",
        proxyUsername: "",
        proxyPassword: "",
        host: "",
        port: "",
        intervalMin: "",
        intervalMax: "",
        pageName: "",
        postType: null,
        file: null,
        isIPV4: false,
        fileNameAsDescription: false,
        customCaptionsEnabled: false,
        captionFallback: "none",
        useCustomProxy: false,
        collabId: null,
        collabName: null,
        hashtagPool: null,
        isStory: false,
      },
      retryCount: 0,
      loadingPages: false,
      pageOptions: [],
      submitting: false,
      uploadProgress: 0,
      dropzoneOptions: {
        url: "dummy", // Required but not used since we handle the file manually
        thumbnailWidth: 150,
        maxFilesize: 10000, // MB
        addRemoveLinks: true,
        acceptedFiles: ".zip",
        autoProcessQueue: false,
        maxFiles: 1,
      },
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    permissions() {
      return this.$store.state.user.permissions;
    },
    isPhoto() {
      return this.form.postType === 0;
    },
    canConfigureQueue() {
      return Boolean(this.form.pageId && this.form.postType !== null);
    },
    selectedPageName() {
      const selectedPage = this.pageOptions.find(
        (page) => page.value === this.form.pageId
      );
      return selectedPage?.text || this.form.pageName;
    },
    selectedPostTypeLabel() {
      return this.form.postType === 1
        ? "Reel automation"
        : this.form.postType === 0
        ? "Photo automation"
        : "Choose post type";
    },
    pageSelectOptions() {
      return [
        { value: "", text: "Select a page", disabled: true },
        ...this.pageOptions,
      ];
    },
    postTypeSelectOptions() {
      const options = [
        { value: null, text: "Select post type", disabled: true },
      ];

      if (this.permissions.allowReels) {
        options.push({ value: 1, text: "Reel" });
      }
      if (this.permissions.allowPhotos) {
        options.push({ value: 0, text: "Photo" });
      }

      return options;
    },
    captionFallbackOptions() {
      return [
        { value: "none", text: "No fallback" },
        { value: "filename", text: "Use filename fallback" },
      ];
    },
    yesNoOptions() {
      return [
        { value: true, text: "Yes" },
        { value: false, text: "No" },
      ];
    },
    cadenceLabel() {
      if (!this.form.intervalMin || !this.form.intervalMax) {
        return "Cadence";
      }
      return `${this.form.intervalMin} - ${this.form.intervalMax} min`;
    },
    submitButtonText() {
      if (this.submitting) return "Submitting...";
      if (this.form.postType === 0 && this.form.customCaptionsEnabled) {
        return "Upload & Prepare Captions";
      }
      return "Create Automation";
    },
    submissionHint() {
      if (this.form.postType === 0 && this.form.customCaptionsEnabled) {
        return "You will go to caption prep after upload.";
      }
      return "Scheduling starts after the first successful post.";
    },
    validationErrors() {
      const errors = [];
      const min = Number(this.form.intervalMin);
      const max = Number(this.form.intervalMax);

      if (!this.form.name) errors.push("Automation name is required.");
      if (!this.form.accessToken) errors.push("Access token is required.");
      if (!this.form.pageId) errors.push("Facebook page is required.");
      if (!this.form.pageName) errors.push("Page name is required.");
      if (this.form.postType === null) errors.push("Post type is required.");
      if (!this.form.intervalMin) errors.push("Minimum interval is required.");
      if (!this.form.intervalMax) errors.push("Maximum interval is required.");
      if (!this.form.file) errors.push("ZIP file is required.");

      if (this.form.intervalMin && (!Number.isFinite(min) || min <= 0)) {
        errors.push("Minimum interval must be greater than 0.");
      }

      if (this.form.intervalMax && (!Number.isFinite(max) || max <= 0)) {
        errors.push("Maximum interval must be greater than 0.");
      }

      if (
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min > 0 &&
        max > 0 &&
        max < min
      ) {
        errors.push("Maximum interval must be greater than or equal to minimum interval.");
      }

      if (this.form.file && !this.form.file.name?.toLowerCase().endsWith(".zip")) {
        errors.push("Upload file must be a ZIP.");
      }

      if (
        this.form.useCustomProxy &&
        (!this.form.proxyUsername ||
          !this.form.proxyPassword ||
          !this.form.host ||
          !this.form.port)
      ) {
        errors.push("All custom proxy fields are required when custom proxy is enabled.");
      }

      return errors;
    },
    canSubmit() {
      return this.validationErrors.length === 0;
    },
  },

  methods: {
    async fetchPages() {
      this.retryCount += 1;

      if (!this.form.accessToken) {
        this.$toast.error("Enter an access token first");
        return;
      }

      if (
        this.form.useCustomProxy &&
        (!this.form.proxyUsername ||
          !this.form.proxyPassword ||
          !this.form.host ||
          !this.form.port)
      ) {
        this.$toast.error("Fill in all custom proxy fields");
        return;
      }

      try {
        this.loadingPages = true;
        this.form.pageId = "";
        this.form.pageName = "";
        const response = await axios.post("/api/get-pages", {
          accessToken: this.form.accessToken,
          isIPV4: this.form.isIPV4,
          proxyUsername: this.form.proxyUsername,
          proxyPassword: this.form.proxyPassword,
          host: this.form.host,
          port: this.form.port,
        });
        this.pageOptions = response.data.pages.map((page) => ({
          value: page.id,
          text: page.name,
        }));
        if (this.pageOptions.length === 1) {
          this.form.pageId = this.pageOptions[0].value;
          this.updatePageDetails();
        }
        if (this.form.postType === null && this.postTypeSelectOptions.length === 2) {
          this.form.postType = this.postTypeSelectOptions[1].value;
        }
        this.loadingPages = false;
      } catch {
        this.loadingPages = false;
        this.$toast.error("Failed to fetch pages");
      }
    },
    updatePageDetails() {
      const selectedPage = this.pageOptions.find(
        (page) => page.value === this.form.pageId
      );
      if (selectedPage) {
        this.form.pageName = selectedPage.text;
      }
    },
    async submitAutomation() {
      this.updatePageDetails();

      if (!this.canSubmit) {
        this.$toast.error(this.validationErrors[0]);
        return;
      }

      const formData = new FormData();
      this.submitting = true;
      this.uploadProgress = 0;

      const payload = {
        ...this.form,
        customCaptionsEnabled:
          this.form.postType === 0 && this.form.customCaptionsEnabled,
        captionFallback:
          this.form.postType === 0 && this.form.customCaptionsEnabled
            ? this.form.captionFallback
            : "none",
        fileNameAsDescription:
          this.form.postType === 0 && this.form.customCaptionsEnabled
            ? this.form.captionFallback === "filename"
            : this.form.fileNameAsDescription,
      };

      for (const key in payload) {
        formData.append(key, payload[key]);
      }

      const url =
        this.form.postType === 1 ? "/schedule-reels" : "/schedule-posts";

      try {
        const response = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
          },
        });
        this.$toast.success(
          response.data?.draft
            ? "Photos uploaded. Prepare captions next."
            : "Automation Created Successfully"
        );
        setTimeout(() => {
          this.uploadProgress = 0;
          if (response.data?.draft && response.data?.automationId) {
            router.push(`/automation-detail/${response.data.automationId}/captions`);
          } else {
            router.push("/dashboard");
          }
        }, 1500);
      } catch {
        this.$toast.error("Failed to create automation");
      } finally {
        this.submitting = false;
        this.uploadProgress = 0;
      }
    },
    handleFileAdded(file) {
      this.form.file = file;
      console.log(this.form.file);
    },
    handleFileRemoved() {
      this.form.file = null;
    },
  },
};
</script>
